/**
 * Voice Profile List Component
 *
 * Displays a list of voice profiles with preview, filtering, and management actions.
 * Supports tenant-scoped voice listing for multi-tenant environments.
 * Voice profiles are used for Chatterbox TTS voice cloning.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

/**
 * Voice profile data structure
 */
interface VoiceProfile {
  id: string;
  name: string;
  description: string | null;
  referenceAudio: string;
  emotionControl: EmotionControl | null;
  isGlobal: boolean;
  tenantId: string | null;
  createdById: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Emotion control parameters for voice cloning
 */
interface EmotionControl {
  happiness?: number;
  sadness?: number;
  anger?: number;
  fear?: number;
  surprise?: number;
  disgust?: number;
}

/**
 * API response types
 */
interface VoiceProfilesResponse {
  voiceProfiles: VoiceProfile[];
  total: number;
}

interface VoiceActionResponse {
  success: boolean;
  message?: string;
}

/**
 * Filter state
 */
type StatusFilter = 'all' | 'active' | 'inactive' | 'global' | 'tenant';

// =============================================================================
// Constants
// =============================================================================

interface StatusColors {
  bg: string;
  text: string;
  dot: string;
}

const STATUS_COLORS: Record<'active' | 'inactive' | 'global', StatusColors> = {
  active: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  inactive: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400' },
  global: { bg: 'bg-purple-900/20', text: 'text-purple-400', dot: 'bg-purple-400' },
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format a timestamp for display
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Get status display info for a voice profile
 */
function getProfileStatus(profile: VoiceProfile): {
  label: string;
  colors: StatusColors;
} {
  if (profile.isGlobal) {
    return { label: 'Global', colors: STATUS_COLORS.global };
  }
  if (profile.active) {
    return { label: 'Active', colors: STATUS_COLORS.active };
  }
  return { label: 'Inactive', colors: STATUS_COLORS.inactive };
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
interface StatusBadgeProps {
  profile: VoiceProfile;
}

function StatusBadge({ profile }: StatusBadgeProps) {
  const { label, colors } = getProfileStatus(profile);

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {label}
    </div>
  );
}

/**
 * Voice profile card component for grid view
 */
interface VoiceCardProps {
  profile: VoiceProfile;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, active: boolean) => void;
  isLoading: boolean;
  showTenant?: boolean;
}

function VoiceCard({
  profile,
  onDelete,
  onToggleActive,
  isLoading,
  showTenant,
}: VoiceCardProps) {
  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-4 transition-all hover:border-discord-blurple hover:shadow-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <Link
            to={`/voices/${profile.id}`}
            className="font-semibold text-white hover:text-discord-blurple transition-colors truncate block"
          >
            {profile.name}
          </Link>
          {profile.description && (
            <p className="mt-1 text-sm text-gray-400 truncate">{profile.description}</p>
          )}
          {showTenant && (
            <p className="mt-1 text-xs text-gray-500">
              {profile.isGlobal ? 'Global Resource' : `Tenant: ${profile.tenantId}`}
            </p>
          )}
        </div>
        <StatusBadge profile={profile} />
      </div>

      {/* Voice Preview/Info */}
      <div className="mt-3 flex items-center gap-2 text-sm text-gray-400">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
        <span className="truncate">Voice clone profile</span>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Created: {formatTimestamp(profile.createdAt)}
        </span>

        <div className="flex items-center gap-2">
          {/* Toggle Active Button */}
          <button
            onClick={() => onToggleActive(profile.id, !profile.active)}
            disabled={isLoading || profile.isGlobal}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              profile.active
                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
            title={profile.active ? 'Deactivate profile' : 'Activate profile'}
          >
            {profile.active ? 'Deactivate' : 'Activate'}
          </button>

          {/* Edit/Configure Button */}
          <Link
            to={`/voices/${profile.id}`}
            className="rounded-md bg-discord-light px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors"
            title="Configure voice profile"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </Link>

          {/* Delete Button */}
          {!profile.isGlobal && (
            <button
              onClick={() => onDelete(profile.id)}
              disabled={isLoading}
              className="rounded-md bg-red-600/20 px-2.5 py-1 text-xs font-medium text-red-400 hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete voice profile"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Voice profile row component for table view
 */
interface VoiceRowProps {
  profile: VoiceProfile;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, active: boolean) => void;
  isLoading: boolean;
  showTenant?: boolean;
}

function VoiceRow({
  profile,
  onDelete,
  onToggleActive,
  isLoading,
  showTenant,
}: VoiceRowProps) {
  return (
    <tr className="border-b border-discord-light hover:bg-discord-light/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <Link
            to={`/voices/${profile.id}`}
            className="font-medium text-white hover:text-discord-blurple transition-colors"
          >
            {profile.name}
          </Link>
          {profile.description && (
            <span className="text-sm text-gray-400 truncate max-w-xs">
              {profile.description}
            </span>
          )}
        </div>
      </td>
      {showTenant && (
        <td className="px-4 py-3 text-sm text-gray-400">
          {profile.isGlobal ? 'Global' : profile.tenantId || '-'}
        </td>
      )}
      <td className="px-4 py-3">
        <StatusBadge profile={profile} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-400">
        {formatTimestamp(profile.createdAt)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleActive(profile.id, !profile.active)}
            disabled={isLoading || profile.isGlobal}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              profile.active
                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {profile.active ? 'Deactivate' : 'Activate'}
          </button>
          <Link
            to={`/voices/${profile.id}`}
            className="rounded-md bg-discord-light px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-discord-blurple hover:text-white transition-colors"
          >
            Edit
          </Link>
          {!profile.isGlobal && (
            <button
              onClick={() => onDelete(profile.id)}
              disabled={isLoading}
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Status filter buttons component
 */
interface StatusFilterProps {
  value: StatusFilter;
  onChange: (filter: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}

function StatusFilterButtons({ value, onChange, counts }: StatusFilterProps) {
  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' },
    { key: 'global', label: 'Global' },
  ];

  return (
    <div className="flex items-center gap-2">
      {filters.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === key
              ? 'bg-discord-blurple text-white'
              : 'bg-discord-light text-gray-300 hover:text-white'
          }`}
        >
          {label}
          {counts[key] > 0 && (
            <span
              className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${
                value === key ? 'bg-white/20' : 'bg-discord-darker'
              }`}
            >
              {counts[key]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Empty state component
 */
interface EmptyStateProps {
  filter: StatusFilter;
}

function EmptyState({ filter }: EmptyStateProps) {
  const messages: Record<StatusFilter, { title: string; description: string }> = {
    all: {
      title: 'No voice profiles yet',
      description: 'Create your first voice profile to enable voice cloning with Chatterbox TTS',
    },
    active: {
      title: 'No active voice profiles',
      description: 'Activate a voice profile to use it with your bots',
    },
    inactive: {
      title: 'No inactive voice profiles',
      description: 'All your voice profiles are currently active',
    },
    global: {
      title: 'No global voice profiles',
      description: 'Global voice profiles are shared across all tenants',
    },
    tenant: {
      title: 'No tenant voice profiles',
      description: 'Create voice profiles specific to your tenant',
    },
  };

  const { title, description } = messages[filter];

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <svg
        className="h-12 w-12 text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
        />
      </svg>
      <h3 className="text-lg font-medium text-white">{title}</h3>
      <p className="mt-1 text-sm text-gray-400">{description}</p>
      {filter === 'all' && (
        <Link
          to="/voices/new"
          className="mt-4 inline-flex items-center rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
        >
          Create Voice Profile
        </Link>
      )}
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="flex items-center justify-between">
        <div className="h-8 w-48 bg-discord-light rounded" />
        <div className="h-8 w-32 bg-discord-light rounded" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="h-5 w-32 bg-discord-light rounded" />
                <div className="h-4 w-48 bg-discord-light rounded" />
              </div>
              <div className="h-6 w-20 bg-discord-light rounded-full" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="h-4 w-24 bg-discord-light rounded" />
              <div className="flex gap-2">
                <div className="h-8 w-8 bg-discord-light rounded" />
                <div className="h-8 w-8 bg-discord-light rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Delete confirmation modal
 */
interface DeleteModalProps {
  isOpen: boolean;
  profileName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function DeleteModal({ isOpen, profileName, onConfirm, onCancel, isLoading }: DeleteModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onCancel}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-lg bg-discord-darker border border-discord-light p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-white">Delete Voice Profile</h3>
        <p className="mt-2 text-sm text-gray-400">
          Are you sure you want to delete &ldquo;{profileName}&rdquo;? This action cannot be undone.
          Any bots using this voice profile will need to be reconfigured.
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Voice profile list page component
 *
 * Displays:
 * - List of voice profiles with status indicators
 * - Filter by status (active/inactive/global)
 * - Management actions (edit, delete, toggle active)
 * - Links to voice profile configuration
 */
export function VoiceList() {
  useAuth(); // Ensure user is authenticated
  const { currentTenant, effectiveTenantId, isGlobalAdmin } = useTenant();

  const [profiles, setProfiles] = useState<VoiceProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<VoiceProfile | null>(null);

  /**
   * Fetch voice profiles from API
   */
  const fetchProfiles = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const queryParams = new URLSearchParams();
      if (effectiveTenantId) {
        queryParams.set('tenantId', effectiveTenantId);
      }
      queryParams.set('includeGlobal', 'true');

      const result = await apiClient.get<VoiceProfilesResponse>(
        `/voices?${queryParams.toString()}`
      );

      if (isSuccess(result)) {
        setProfiles(result.data.voiceProfiles);
      } else {
        setError(result.error.message || 'Failed to fetch voice profiles');
      }
    } catch (err) {
      setError('Failed to load voice profiles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveTenantId]);

  /**
   * Toggle voice profile active status
   */
  const handleToggleActive = useCallback(
    async (profileId: string, active: boolean) => {
      setIsActionLoading(true);
      setError(null);

      const apiClient = getApiClient();

      try {
        const result = await apiClient.put<VoiceActionResponse>(`/voices/${profileId}`, {
          active,
        });

        if (isSuccess(result)) {
          setProfiles((prev) =>
            prev.map((profile) =>
              profile.id === profileId ? { ...profile, active } : profile
            )
          );
        } else {
          setError(result.error.message || 'Failed to update voice profile');
        }
      } catch (err) {
        setError('Failed to update voice profile. Please try again.');
      } finally {
        setIsActionLoading(false);
      }
    },
    []
  );

  /**
   * Delete voice profile
   */
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;

    setIsActionLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.delete<VoiceActionResponse>(`/voices/${deleteTarget.id}`);

      if (isSuccess(result)) {
        setProfiles((prev) => prev.filter((profile) => profile.id !== deleteTarget.id));
        setDeleteTarget(null);
      } else {
        setError(result.error.message || 'Failed to delete voice profile');
      }
    } catch (err) {
      setError('Failed to delete voice profile. Please try again.');
    } finally {
      setIsActionLoading(false);
    }
  }, [deleteTarget]);

  /**
   * Open delete confirmation
   */
  const handleDeleteClick = useCallback(
    (profileId: string) => {
      const profile = profiles.find((p) => p.id === profileId);
      if (profile) {
        setDeleteTarget(profile);
      }
    },
    [profiles]
  );

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchProfiles();
  }, [fetchProfiles]);

  /**
   * Calculate status counts
   */
  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = {
      all: profiles.length,
      active: 0,
      inactive: 0,
      global: 0,
      tenant: 0,
    };

    profiles.forEach((profile) => {
      if (profile.isGlobal) {
        counts.global++;
      } else {
        counts.tenant++;
      }
      if (profile.active) {
        counts.active++;
      } else {
        counts.inactive++;
      }
    });

    return counts;
  }, [profiles]);

  /**
   * Filter and search voice profiles
   */
  const filteredProfiles = useMemo(() => {
    let filtered = profiles;

    // Apply status filter
    switch (statusFilter) {
      case 'active':
        filtered = filtered.filter((p) => p.active);
        break;
      case 'inactive':
        filtered = filtered.filter((p) => !p.active);
        break;
      case 'global':
        filtered = filtered.filter((p) => p.isGlobal);
        break;
      case 'tenant':
        filtered = filtered.filter((p) => !p.isGlobal);
        break;
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (profile) =>
          profile.name.toLowerCase().includes(query) ||
          (profile.description && profile.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [profiles, statusFilter, searchQuery]);

  /**
   * Show tenant column for global admins viewing all tenants
   */
  const showTenant = isGlobalAdmin && !currentTenant;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Voice Profiles</h1>
            <p className="mt-1 text-gray-400">
              {currentTenant
                ? `Manage voice profiles for ${currentTenant.name}`
                : isGlobalAdmin
                  ? 'Manage all voice profiles across tenants'
                  : 'Manage your voice cloning profiles for Chatterbox TTS'}
            </p>
          </div>
          <Link
            to="/voices/new"
            className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Voice Profile
          </Link>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Filters and Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <StatusFilterButtons
                value={statusFilter}
                onChange={setStatusFilter}
                counts={statusCounts}
              />

              <div className="flex items-center gap-3">
                {/* Search Input */}
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search voices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-48 rounded-md border border-discord-light bg-discord-darker py-2 pl-10 pr-4 text-sm text-white placeholder-gray-500 focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>

                {/* View Toggle */}
                <div className="flex rounded-md bg-discord-light">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`rounded-l-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Grid view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`rounded-r-md px-3 py-2 text-sm transition-colors ${
                      viewMode === 'table'
                        ? 'bg-discord-blurple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title="Table view"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                  </button>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={() => void fetchProfiles()}
                  disabled={isLoading}
                  className="rounded-md bg-discord-light p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <svg
                    className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Voice Profile List */}
            {filteredProfiles.length === 0 ? (
              <EmptyState filter={statusFilter} />
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredProfiles.map((profile) => (
                  <VoiceCard
                    key={profile.id}
                    profile={profile}
                    onDelete={handleDeleteClick}
                    onToggleActive={handleToggleActive}
                    isLoading={isActionLoading}
                    showTenant={showTenant}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-discord-light overflow-hidden">
                <table className="w-full">
                  <thead className="bg-discord-darker">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Name
                      </th>
                      {showTenant && (
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                          Scope
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-discord-dark">
                    {filteredProfiles.map((profile) => (
                      <VoiceRow
                        key={profile.id}
                        profile={profile}
                        onDelete={handleDeleteClick}
                        onToggleActive={handleToggleActive}
                        isLoading={isActionLoading}
                        showTenant={showTenant}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary */}
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>
                Showing {filteredProfiles.length} of {profiles.length} voice profiles
              </span>
              <span>
                {statusCounts.active} active · {statusCounts.inactive} inactive
                {statusCounts.global > 0 && ` · ${statusCounts.global} global`}
              </span>
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteModal
          isOpen={deleteTarget !== null}
          profileName={deleteTarget?.name || ''}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          isLoading={isActionLoading}
        />
      </div>
    </Layout>
  );
}

export default VoiceList;
